import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import {
  InspectionBottomTabInfo,
  WiredInspectionBottomTabs,
} from '@euler/app/tv/components/inspection/BottomTabs';
import { WiredModulePage } from '@euler/app/tv/components/inspection/ModulePage';
import { InspectionOverview } from '@euler/app/tv/components/inspection/Overview';
import { SiteInspectionCard } from '@euler/app/tv/components/inspection/types';
import { TaskScreenContent } from '@euler/app/tv/components/TaskScreenContent';
import { useSystemModuleStats } from '@euler/app/tv/functions/useSystemModuleStats';
import {
  useScreenContentMarginStyle,
  useScreenContentPaddingStyle,
} from '@euler/app/tv/functions/utils';
import type { TaskNavParams } from '@euler/app/tv/pages/TaskFlow';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { DefectiveLevel } from '@euler/model';
import { VehicleSystemModule } from '@euler/model/entity';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useEffect, useState } from 'react';
import { useWindowDimensions } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const InspectionScreen = wrapNavigatorScreen(
  ({ flowId }: { flowId?: string }) => {
    const navigation = useNavigation<StackNavigationProp<TaskNavParams>>();
    const { rpx } = useResponsiveContext();
    const { detail } = useTaskContext();
    const moduleStats = useSystemModuleStats();
    const { width } = useWindowDimensions();
    const { bottom } = useSafeAreaInsets();
    const [systemModules, setSystemModules] = useState<VehicleSystemModule[]>();
    const [activeIndex, setActiveIndex] = useState(0);
    const translateX = useSharedValue(0);
    const contentMarginStyle = useScreenContentMarginStyle();
    const contentPaddingStyle = useScreenContentPaddingStyle();
    const [activeTab, setActiveTab] = useState<InspectionBottomTabInfo>({
      type: 'overview',
    });

    const onSystemModulesLoaded = useCallback(
      (modules: VehicleSystemModule[]) => {
        setSystemModules(modules);
      },
      [],
    );

    const count = systemModules != null ? systemModules.length + 2 : 1;

    const onTabPress = useCallback(
      (tabInfo: InspectionBottomTabInfo) => {
        if (systemModules == null) return;
        const index =
          tabInfo.type === 'overview'
            ? 0
            : tabInfo.type === 'module'
            ? 1 + systemModules.findIndex(x => x.id === tabInfo.mod.id)
            : count - 1;
        if (index === activeIndex) return;
        setActiveIndex(index);
        setActiveTab(tabInfo);
      },
      [activeIndex, count, systemModules],
    );

    useEffect(() => {
      translateX.value = withTiming(-activeIndex * width, { duration: 250 });
    }, [activeIndex, translateX, width]);

    const animatedStyle = useAnimatedStyle(() => {
      return { transform: [{ translateX: translateX.value }] };
    });

    const onCardPress = usePersistFn((card: SiteInspectionCard) => {
      if (activeTab.type === 'overview') return;

      const currentSystemModId =
        activeTab.type === 'module' ? activeTab.mod.id : 0;

      navigation.navigate('_inspectedSiteDetail', {
        filter: {
          type: 'system-module',
          currentSystemModId,
          target: {
            type: card.type,
            id:
              card.type === 'inspected-site'
                ? card.inspectedSite.id
                : card.customIssue.id,
            key: card.key,
          },
        },
      });
    });

    const onChartCellPress = usePersistFn((defectiveLevel: DefectiveLevel) => {
      navigation.navigate('_inspectedSiteDetail', {
        filter: {
          type: 'defective-level',
          defectiveLevel,
        },
      });
    });

    return (
      <TaskScreenContent
        targetStatus="inspection"
        css={`
          padding-bottom: 0;
        `}
      >
        <LayoutProviderView
          css={`
            flex: 1;
            overflow: hidden;
          `}
        >
          <Animated.View
            css={`
              flex: 1;
              flex-direction: row;
              align-items: stretch;
              flex-wrap: nowrap;
              width: ${count * width}px;
            `}
            style={animatedStyle}
          >
            <InspectionOverview
              detail={detail}
              flowId={flowId}
              onChartCellPress={onChartCellPress}
              style={contentPaddingStyle}
              css={`
                width: ${width}px;
                top: ${-bottom / 2}px;
              `}
            />
            {systemModules != null && (
              <>
                {systemModules.map(mod => (
                  <WiredModulePage
                    key={mod.id}
                    modId={mod.id}
                    modName={mod.name}
                    mod={mod}
                    width={width}
                    onCardPress={onCardPress}
                    active={
                      activeTab.type === 'module' && activeTab.mod.id === mod.id
                    }
                  />
                ))}
              </>
            )}
            <WiredModulePage
              modId={0}
              modName={'其他'}
              width={width}
              onCardPress={onCardPress}
              active={activeTab.type === 'others'}
            />
          </Animated.View>
        </LayoutProviderView>
        <WiredInspectionBottomTabs
          moduleStats={moduleStats}
          onSystemModulesLoaded={onSystemModulesLoaded}
          activeTab={activeTab}
          onTabPress={onTabPress}
          style={contentMarginStyle}
          css={`
            height: ${rpx(148) + bottom / 2}px;
            padding-bottom: ${bottom / 2}px;
          `}
        />
      </TaskScreenContent>
    );
  },
);
